// AutoBIM Logic - Web i18n (mirrors mobile src/lib/i18n.ts).
// Language-agnostic: add a locale by dropping <code>.json into ./locales
// and registering it in BUNDLES below.
import en from "./locales/en.json";
import ar from "./locales/ar.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import pt from "./locales/pt.json";
import tr from "./locales/tr.json";
import ur from "./locales/ur.json";
import hi from "./locales/hi.json";
import ru from "./locales/ru.json";
import zh from "./locales/zh.json";
import ja from "./locales/ja.json";
import fa from "./locales/fa.json";
import id from "./locales/id.json";
import fil from "./locales/fil.json";

export const LANGUAGES = [
  { code: "en", nativeName: "English", englishName: "English", flag: "🇬🇧", rtl: false },
  { code: "ar", nativeName: "العربية", englishName: "Arabic", flag: "🇸🇦", rtl: true },
  { code: "es", nativeName: "Español", englishName: "Spanish", flag: "🇪🇸", rtl: false },
  { code: "fr", nativeName: "Français", englishName: "French", flag: "🇫🇷", rtl: false },
  { code: "de", nativeName: "Deutsch", englishName: "German", flag: "🇩🇪", rtl: false },
  { code: "pt", nativeName: "Português", englishName: "Portuguese", flag: "🇵🇹", rtl: false },
  { code: "tr", nativeName: "Türkçe", englishName: "Turkish", flag: "🇹🇷", rtl: false },
  { code: "ur", nativeName: "اُردُو", englishName: "Urdu", flag: "🇵🇰", rtl: true },
  { code: "hi", nativeName: "हिन्दी", englishName: "Hindi", flag: "🇮🇳", rtl: false },
  { code: "ru", nativeName: "Русский", englishName: "Russian", flag: "🇷🇺", rtl: false },
  { code: "zh", nativeName: "中文", englishName: "Chinese", flag: "🇨🇳", rtl: false },
  { code: "ja", nativeName: "日本語", englishName: "Japanese", flag: "🇯🇵", rtl: false },
  { code: "fa", nativeName: "فارسی", englishName: "Persian", flag: "🇮🇷", rtl: true },
  { code: "id", nativeName: "Bahasa Indonesia", englishName: "Indonesian", flag: "🇮🇩", rtl: false },
  { code: "fil", nativeName: "Filipino", englishName: "Filipino", flag: "🇵🇭", rtl: false },
];

const BUNDLES = { en, ar, es, fr, de, pt, tr, ur, hi, ru, zh, ja, fa, id, fil };

export const isRTL = (code) => LANGUAGES.find((l) => l.code === code)?.rtl ?? false;

const LANG_KEY = "autobimlogic:language";
const LANG_PICKED_KEY = "autobimlogic:language_picked";

export function getStoredLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v && BUNDLES[v]) return v;
  } catch {}
  return null;
}

export function setStoredLang(code) {
  try {
    localStorage.setItem(LANG_KEY, code);
    localStorage.setItem(LANG_PICKED_KEY, "1");
  } catch {}
}

export function hasPickedLanguage() {
  try {
    return localStorage.getItem(LANG_PICKED_KEY) === "1";
  } catch {
    return false;
  }
}

export function detectDeviceLang() {
  let raw = "en";
  try {
    if (typeof navigator !== "undefined" && navigator.language) raw = navigator.language;
  } catch {}
  const short = String(raw).toLowerCase().split(/[-_]/)[0];
  const match = LANGUAGES.find((l) => l.code === short);
  return match?.code || "en";
}

// Read ?lang=xx from the URL (used by SEO hreflang deep-links).
export function getQueryLang() {
  try {
    const p = new URLSearchParams(window.location.search).get("lang");
    if (p && BUNDLES[p]) return p;
  } catch {}
  return null;
}

export function loadLang() {
  return getStoredLang() || getQueryLang() || detectDeviceLang();
}

// Localized document <title>/<meta description> for the top languages - helps
// search engines show localized snippets. Others fall back to English.
const SEO_META = {
  en: { t: "AutoBIM Logic - AI-grade Architectural & BIM Plan Generator", d: "Generate complete architectural & structural plans in one click. AutoCAD precision, DXF/PDF/IFC/BOQ, 15 languages." },
  ar: { t: "أوتوبيم لوجيك - مولّد المخططات المعمارية والإنشائية", d: "أنشئ مخططات معمارية وإنشائية كاملة بضغطة واحدة بدقّة أوتوكاد وتصدير DXF/PDF/IFC وبـ15 لغة." },
  es: { t: "AutoBIM Logic - Generador de planos arquitectónicos y BIM con IA", d: "Genera planos arquitectónicos y estructurales completos con un clic. Precisión AutoCAD, DXF/PDF/IFC, 15 idiomas." },
};

export function saveLang(code) {
  setStoredLang(code);
}

export function applyRTL(code) {
  const rtl = isRTL(code);
  try {
    if (typeof document !== "undefined") {
      document.documentElement.dir = rtl ? "rtl" : "ltr";
      document.documentElement.lang = code;
      const m = SEO_META[code] || SEO_META.en;
      document.title = m.t;
      const md = document.querySelector('meta[name="description"]');
      if (md) md.setAttribute("content", m.d);
    }
  } catch {}
}

export function t(key, lang) {
  const code = lang || "en";
  const bundle = BUNDLES[code] || BUNDLES.en;
  return bundle[key] || BUNDLES.en[key] || key;
}

export const regions = {
  GLOBAL: { ar: "عالمي · كل الأكواد", en: "Global · All Codes" },
  SA: { ar: "السعودية (SBC)", en: "Saudi Arabia (SBC)" },
  AE: { ar: "الإمارات (UAE code)", en: "UAE" },
  EG: { ar: "مصر (ECP)", en: "Egypt (ECP)" },
  JO: { ar: "الأردن", en: "Jordan" },
  KW: { ar: "الكويت", en: "Kuwait" },
  QA: { ar: "قطر (QCS)", en: "Qatar (QCS)" },
  OM: { ar: "عُمان", en: "Oman" },
  BH: { ar: "البحرين", en: "Bahrain" },
  US: { ar: "الولايات المتحدة (IBC/ACI)", en: "USA (IBC/ACI)" },
  EU: { ar: "أوروبا (Eurocode)", en: "Europe (Eurocode)" },
  UK: { ar: "المملكة المتحدة (BS)", en: "UK (BS)" },
  IN: { ar: "الهند (NBC)", en: "India (NBC)" },
  TR: { ar: "تركيا (TBDY)", en: "Turkey (TBDY)" },
  MA: { ar: "المغرب", en: "Morocco" },
  DZ: { ar: "الجزائر", en: "Algeria" },
  TN: { ar: "تونس", en: "Tunisia" },
};

export function tRegion(code, lang) {
  const r = regions[code];
  if (!r) return code;
  return lang === "ar" ? r.ar : r.en;
}

export const dict = BUNDLES;
